import { Fan, Gauge, type Icon, Sun, ThermometerSimple } from "@phosphor-icons/react";
import { useState } from "react";

import {
  type AdminHouseholdDetail,
  getApiErrorMessage,
  type SystemPresence,
  useUpdatePreconfig,
} from "@/lib/api";
import { cn } from "@/lib/utils";

interface SystemsCardProps {
  household: AdminHouseholdDetail;
}

type SystemKey = "roof_windows" | "ventilation" | "floor_heating" | "air_quality";

/** The four onboarding systems, in flow order, with the hardware each needs. */
const SYSTEMS: { key: SystemKey; label: string; hardware: string; icon: Icon }[] = [
  {
    key: "roof_windows",
    label: "Roof windows",
    hardware: "VELUX ACTIVE · KLG300 gateway",
    icon: Sun,
  },
  { key: "ventilation", label: "Ventilation", hardware: "Nilan gateway", icon: Fan },
  {
    key: "floor_heating",
    label: "Floor heating",
    hardware: "Wavin gateway",
    icon: ThermometerSimple,
  },
  { key: "air_quality", label: "Air quality", hardware: "AirThings sensors", icon: Gauge },
];

/** Orange-when-on pill switch, built locally (no shadcn Switch in this project). */
function Switch({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        /* The track and knob stay capsule/circle: a switch reads as a switch
           because of that geometry, and squaring it off turns it into a
           checkbox. "On" is brand Black — a state, not an action. */
        "focus-visible:outline-ring relative h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50",
        checked ? "bg-accent" : "bg-border-strong",
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 size-5 rounded-full bg-white transition-all",
          checked ? "left-[22px]" : "left-0.5",
        )}
      />
    </button>
  );
}

/**
 * House-level toggles for which climate systems a household actually has.
 * Turning one off removes its pre-ship hardware from the readiness check
 * (the backend gates `preconfig_missing` on these flags), so a home without a
 * given system can still reach "pre-ship ready". Persisted into the preconfig
 * jsonb via `useUpdatePreconfig` — the whole object is sent on every change.
 */
export function SystemsCard({ household }: SystemsCardProps) {
  const updateMutation = useUpdatePreconfig();
  const [error, setError] = useState("");

  const systems = household.preconfig.systems ?? {};
  const isOn = (key: SystemKey) => systems[key] ?? true;

  const toggle = (key: SystemKey, next: boolean) => {
    setError("");
    const body: SystemPresence = {
      roof_windows: isOn("roof_windows"),
      ventilation: isOn("ventilation"),
      floor_heating: isOn("floor_heating"),
      air_quality: isOn("air_quality"),
      [key]: next,
    };
    void updateMutation.mutateAsync({ id: household.id, body: { systems: body } }).catch((err) => {
      setError(getApiErrorMessage(err, "Couldn't update systems."));
    });
  };

  return (
    <div className="bg-card shadow-elevation rounded-md">
      <div className="border-border-subtle border-b px-5 py-[18px]">
        <h2 className="text-primary text-[17px] font-bold">Systems</h2>
        <p className="text-muted mt-1 text-[13px] leading-[1.4]">
          Turn off a system this home doesn&apos;t have — its pre-ship hardware stops counting
          toward handoff readiness.
        </p>
      </div>
      <div className="flex flex-col">
        {SYSTEMS.map(({ key, label, hardware, icon: SystemIcon }, i) => {
          const on = isOn(key);
          return (
            <div
              key={key}
              className={cn(
                "flex items-center gap-3.5 px-5 py-3.5",
                i > 0 && "border-border-subtle border-t",
              )}
            >
              <span
                className={cn(
                  "bg-background grid size-9 shrink-0 place-items-center rounded-xl",
                  !on && "opacity-45",
                )}
              >
                <SystemIcon className="text-muted size-5" weight="light" />
              </span>
              <div className="min-w-0 grow">
                <div
                  className={cn("text-[14.5px] font-semibold", on ? "text-primary" : "text-muted")}
                >
                  {label}
                </div>
                <div className="text-muted text-[12.5px]">
                  {on ? hardware : "Not installed · excluded from handoff"}
                </div>
              </div>
              <Switch
                checked={on}
                disabled={updateMutation.isPending}
                onChange={(next) => toggle(key, next)}
                label={`${label} installed`}
              />
            </div>
          );
        })}
      </div>
      {error && <p className="text-destructive px-5 pb-3.5 text-[12.5px]">{error}</p>}
    </div>
  );
}
